/**
 * KAN-727: column mapping helpers of the list upload page.
 *
 * - autoSelectMapping: pick a file column for every schema field once the
 *   headers are known (saved preset first, then label/name match);
 * - mappingFromPreset: defaults overlaid by a remembered mapping;
 * - extractMappingFieldErrors: per-field "column not found" errors of a 400.
 */
import type { ColumnMapping, SchemaField, UploadPreset } from '@/types/dataset';

const norm = (value: string | undefined | null): string =>
  (value ?? '').trim().toLowerCase();

/** Shortest text that counts for a "contains" match (avoids "a" in "Имя"). */
const MIN_CONTAINS_LENGTH = 3;

function containsMatch(header: string, field: SchemaField): boolean {
  const h = norm(header);
  return [field.label, field.name].some((raw) => {
    const term = norm(raw);
    if (term.length < MIN_CONTAINS_LENGTH || h.length < MIN_CONTAINS_LENGTH) {
      return false;
    }
    return h.includes(term) || term.includes(h);
  });
}

/**
 * Column for each schema field among `headers`; '' when nothing fits.
 *
 * Order: the saved column (if still present), an exact case-insensitive match
 * of the label or name, then a "contains" match over the columns no other field
 * has taken yet. One column may still feed several fields through the saved
 * mapping (e.g. Telegram and Phone from one contact column).
 */
export function autoSelectMapping(
  schema: SchemaField[],
  headers: string[],
  saved: ColumnMapping = {}
): ColumnMapping {
  const byNorm = new Map(headers.map((h) => [norm(h), h]));
  const result: ColumnMapping = {};
  const used = new Set<string>();

  schema.forEach((field) => {
    const savedColumn = saved[field.name];
    const pick =
      (savedColumn && headers.includes(savedColumn) ? savedColumn : undefined) ??
      byNorm.get(norm(field.label)) ??
      byNorm.get(norm(field.name));
    result[field.name] = pick ?? '';
    if (pick) used.add(pick);
  });

  schema.forEach((field) => {
    if (result[field.name]) return;
    const pick = headers.find((h) => !used.has(h) && containsMatch(h, field));
    if (pick) {
      result[field.name] = pick;
      used.add(pick);
    }
  });
  return result;
}

/** Schema defaults overlaid by a remembered mapping (only schema fields). */
export function mappingFromPreset(
  defaults: ColumnMapping,
  saved: ColumnMapping
): ColumnMapping {
  const result: ColumnMapping = { ...defaults };
  Object.entries(saved).forEach(([field, column]) => {
    if (field in defaults && column) {
      result[field] = column;
    }
  });
  return result;
}

/** Recent presets that carry a Google Sheets link. */
export function recentSheetPresets(presets: UploadPreset[] | undefined): UploadPreset[] {
  return (presets ?? []).filter((preset) => !!preset.sheet_url);
}

/** "TG список, 08.10.2026" for the recent-link picker. */
export function presetLabel(preset: UploadPreset): string {
  const date = new Date(preset.created_at);
  const day = Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('ru-RU');
  const name = preset.dataset_name || 'Список';
  return day ? `${name}, ${day}` : name;
}

/** `{field: message}` from a 400 `column_mapping_fields`, else {}. */
export function extractMappingFieldErrors(err: unknown): Record<string, string> {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== 'object') return {};
  const raw = (data as { column_mapping_fields?: unknown }).column_mapping_fields;
  if (!raw || typeof raw !== 'object') return {};
  const result: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([field, value]) => {
    const message = Array.isArray(value) ? value[0] : value;
    if (typeof message === 'string' && message) {
      result[field] = message;
    }
  });
  return result;
}
